/**
 * 🆕 ConfirmContext — confirm imperativo via Promise
 *
 * Substitui o `window.confirm()` (que bloqueia o event loop e tem UX feia)
 * por uma versão moderna baseada em ConfirmModal. Uso:
 *
 *   const { confirm } = useConfirm();
 *   ...
 *   const ok = await confirm({
 *     title: 'Remover conta?',
 *     message: 'Essa ação não pode ser desfeita.',
 *     variant: 'danger',
 *     confirmLabel: 'Remover',
 *   });
 *   if (!ok) return;
 *
 * Provider deve ficar no nível do App. Apenas 1 modal por vez na tela.
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import ConfirmModal from '../theme/components/ConfirmModal';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({ show: false });

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({
        show: true,
        ...options,
        onConfirm: () => {
          setState({ show: false });
          resolve(true);
        },
        onCancel: () => {
          setState({ show: false });
          resolve(false);
        },
      });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmModal
        show={state.show}
        onHide={state.onCancel}
        onConfirm={state.onConfirm}
        title={state.title}
        message={state.message}
        details={state.details}
        variant={state.variant}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        size={state.size}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm deve ser usado dentro de <ConfirmProvider>');
  }
  return ctx;
};

export default ConfirmContext;
